import type { TranslateService } from '@ngx-translate/core';
import type { Uuid } from '@common/types';
import type { ContentSource } from '@common/types/template.type';
import { type ELanguages, ESiteStatuses } from '@common/enums';

export type LibraryBlockEntity = {
    id: number;
    blockId: string;
    name: string;
    lang: ELanguages;
    categoryIds: LibraryBlockCategory[];
    // content: Content[];
    content: any;
    thumbnail: string;
    tempStatus: TempStatus;
    templateId?: string | number;
    userId?: number | null;
};

export type UnsavedLibraryBlockEntity = Omit<LibraryBlockEntity, 'id' | 'content'> & {};

export type LibraryBlockEntityType = LibraryBlockEntity | UnsavedLibraryBlockEntity;

export type LibraryBlockApiEntity = {
    id: number;
    blockId: string;
    name: string;
    lang: ELanguages;
    status: ESiteStatuses;
    categories: LibraryBlockApiCategory[];
    content: ContentApi;
    contentSource: ContentSource;
    thumb?: null;
    description?: string | null;
    thumbUrl?: string;
    userId?: number;
};

export enum TempStatus {
    new = 'new',
    created = 'created',
    deleted = 'deleted',
}

export enum LibraryBlockCategory {
    saved = -1,
    widgets = 0, // only front, not really a category
    header,
    firstScreen,
    aboutUs,
    advantages,
    text,
    contacts,
    faq,
    footer,
    productsAndServices,
    form,
    pricing,
    timeline,
}

export type LibraryBlockApiCategory = {
    id: LibraryBlockCategory;
    name: keyof typeof LibraryBlockCategory;
    description: string;
    langs: string[];
};

export const libraryBlockCategoryTranslations = (translateService: TranslateService) => ({
    [LibraryBlockCategory.header]: translateService.instant('category_header'),
    [LibraryBlockCategory.firstScreen]: translateService.instant('category_first_screen'),
    [LibraryBlockCategory.aboutUs]: translateService.instant('category_about_us'),
    [LibraryBlockCategory.text]: translateService.instant('category_text'),
    [LibraryBlockCategory.productsAndServices]: translateService.instant('category_products_and_services'),
    [LibraryBlockCategory.advantages]: translateService.instant('category_advantages'),
    [LibraryBlockCategory.timeline]: translateService.instant('category_timeline'),
    [LibraryBlockCategory.pricing]: translateService.instant('category_pricing'),
    [LibraryBlockCategory.faq]: translateService.instant('category_faq'),
    [LibraryBlockCategory.form]: translateService.instant('category_forms'),
    [LibraryBlockCategory.contacts]: translateService.instant('category_contacts'),
    [LibraryBlockCategory.footer]: translateService.instant('category_footer'),
    [LibraryBlockCategory.widgets]: translateService.instant('category_widgets'),
    [LibraryBlockCategory.saved]: translateService.instant('category_saved'),
});

export const libraryBlockCategoryOrder = [
    LibraryBlockCategory.saved,
    LibraryBlockCategory.header,
    LibraryBlockCategory.firstScreen,
    LibraryBlockCategory.aboutUs,
    LibraryBlockCategory.text,
    LibraryBlockCategory.productsAndServices,
    LibraryBlockCategory.advantages,
    LibraryBlockCategory.timeline,
    LibraryBlockCategory.pricing,
    LibraryBlockCategory.faq,
    LibraryBlockCategory.form,
    LibraryBlockCategory.contacts,
    LibraryBlockCategory.footer,
    LibraryBlockCategory.widgets,
];

type ContentApi = {
    html: string;
    structure: string;
    theme_settings: string;
    theme_settings_style: string;
};

export const prepareDataForStore = (data: LibraryBlockApiEntity[]): LibraryBlockEntity[] => {
    if (!data) {
        return [];
    }
    return data
        .filter((i) => i.status === ESiteStatuses.Active)
        .map((item) => {
            try {
                return {
                    id: item.id,
                    blockId: item.blockId,
                    name: item.name,
                    lang: item.lang,
                    categoryIds: item.categories.map((category) => category.id),
                    content: {
                        ...item.content,
                        structure: JSON.parse(item.content.structure),
                    },
                    thumbnail: item.thumbUrl,
                    tempStatus: TempStatus.created,
                    templateId: item.id,
                    userId: item.userId,
                };
            } catch (e) {
                console.error(
                    `Error while parsing library block with ID: ${item.id}, block entity ID: ${item.blockId}`,
                    e
                );
                return null;
            }
        })
        .filter((i) => i !== null) as LibraryBlockEntity[];
};
